import React, { useEffect } from "react";
import {
  Shield, Lock, Zap, BarChart2, CheckCircle,
  ArrowRight, ChevronRight, Settings, Users, BarChart, PlayCircle, ShieldCheck
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const AiGov = () => {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  return (
    <div className="text-[#2c2c2c] overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden bg-white/40 border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="flex flex-col lg:flex-row items-center justify-between gap-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div className="lg:w-1/2 space-y-6" variants={fadeInUp}>
              <motion.div
                className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20"
                variants={fadeInUp}
              >
                <Shield className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider">AI GOVERNANCE</span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900"
                variants={fadeInUp}
              >
                Professional,<br />
                <span className="text-[#005a9c]">Preconfigured</span> for <br />
                Enterprise Action
              </motion.h1>

              <motion.p
                className="text-lg text-slate-600 max-w-2xl leading-relaxed"
                variants={fadeInUp}
              >
                Assess, validate, and accelerate your AI transformation with
                <span className="font-semibold text-slate-900"> RAKFORT</span>'s
                comprehensive enterprise readiness platform.
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-4 mt-8"
                variants={fadeInUp}
              >
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group relative bg-[#005a9c] text-white font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-[#003d6b] flex items-center shadow-lg"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>START ASSESSMENT</span>
                    <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>

                <a
                  href="https://youtu.be/8ebSV65kN3s"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-transparent border-2 border-slate-300 text-slate-700 font-medium px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-slate-50 flex items-center"
                >
                  <span className="relative z-10 flex items-center">
                    <PlayCircle className="w-5 h-5 mr-2 text-[#005a9c]" />
                    <span>WATCH DEMO</span>
                  </span>
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 relative"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-[#e5eef6] shadow-xl">
                <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
                  <ShieldCheck className="w-6 h-6 text-[#005a9c] mr-2" />
                  Compliance Frameworks
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { text: 'EU AI Act', icon: <Shield className="w-4 h-4" /> },
                    { text: 'NIST AI RMF', icon: <Shield className="w-4 h-4" /> },
                    { text: 'ISO 42001', icon: <Shield className="w-4 h-4" /> },
                    { text: 'CO Life Insurance Reg', icon: <Shield className="w-4 h-4" /> }
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center space-x-2 bg-slate-50 px-4 py-2.5 rounded-lg border border-slate-100"
                      whileHover={{ x: 4 }}
                    >
                      <div className="text-[#005a9c]">{item.icon}</div>
                      <span className="text-sm font-medium text-slate-700">{item.text}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-600">Readiness Score</span>
                    <span className="text-sm font-bold text-[#005a9c]">85%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-[#005a9c] h-2 rounded-full"
                      style={{ width: '85%' }}
                    ></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 border-b border-[#e5eef6] bg-[#f0f7ff]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20 mb-6">
                <Shield className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider">AI READINESS</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                How We Make AI Adoption <span className="text-[#005a9c]">Reliable</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                RAKFORT reduces AI implementation risks while accelerating
                time-to-value through proven methodologies and comprehensive
                assessment frameworks.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {[
              {
                percent: "85%",
                title: "Risk Mitigation",
                desc: "Proactive identification and mitigation of AI-related risks before deployment.",
                icon: <Shield className="w-8 h-8 text-yellow-600" />
              },
              {
                percent: "95%",
                title: "Compliance",
                desc: "Automated compliance tracking with industry standards and regulations.",
                icon: <CheckCircle className="w-8 h-8 text-[#005a9c]" />
              },
              {
                percent: "92%",
                title: "Team Readiness",
                desc: "Comprehensive training and skill gap analysis for your AI teams.",
                icon: <Users className="w-8 h-8 text-blue-600" />
              },
              {
                percent: "60%",
                title: "Faster Deployment",
                desc: "Accelerated AI implementation with pre-validated frameworks.",
                icon: <Zap className="w-8 h-8 text-green-600" />
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="h-full"
              >
                <div className="h-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-[#e5eef6] shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-slate-50 mb-6 mx-auto">
                    {item.icon}
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-3">{item.percent}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-transparent border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20 mb-6">
                <Settings className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider">PLATFORM FEATURES</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Comprehensive AI <span className="text-[#005a9c]">Readiness Platform</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to assess, validate, and accelerate your
                enterprise AI transformation journey.
              </p>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-6 h-6 text-yellow-600" />,
                title: "AI Security Assessment",
                desc: "Comprehensive security evaluation of your AI infrastructure and data pipelines.",
                color: "yellow"
              },
              {
                icon: <BarChart2 className="w-6 h-6 text-purple-600" />,
                title: "Readiness Analytics",
                desc: "Deep insights into your organization's AI maturity across all departments.",
                color: "purple"
              },
              {
                icon: <Users className="w-6 h-6 text-blue-600" />,
                title: "Team Capability Mapping",
                desc: "Assess and develop your team's AI skills with personalized learning paths.",
                color: "blue"
              },
              {
                icon: <Zap className="w-6 h-6 text-green-600" />,
                title: "Performance Optimization",
                desc: "Identify bottlenecks and optimize your AI workflows for maximum efficiency.",
                color: "green"
              },
              {
                icon: <Lock className="w-6 h-6 text-red-600" />,
                title: "Compliance Framework",
                desc: "Ensure your AI initiatives meet industry standards and regulatory requirements.",
                color: "red"
              },
              {
                icon: <BarChart className="w-6 h-6 text-[#005a9c]" />,
                title: "ROI Tracking",
                desc: "Monitor and measure the business impact of your AI transformation initiatives.",
                color: "indigo"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                className="group"
              >
                <div className="h-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-[#e5eef6] shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-6 group-hover:bg-[#005a9c]/10 transition-colors">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  <div className="mt-6 flex items-center text-sm font-medium text-[#005a9c] hover:text-[#003d6b] transition-colors">
                    Learn more
                    <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Governance in Action Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20 mb-6">
                <PlayCircle className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider">AI GOVERNANCE IN ACTION</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                See How <span className="#005a9c">RAKFORT</span> Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience our AI governance platform through an interactive demo and see how we can help secure your AI initiatives.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="relative bg-white rounded-2xl overflow-hidden shadow-2xl border border-[#e5eef6]"
            variants={fadeInUp}
            whileHover={{ scale: 1.01, transition: { duration: 0.3 } }}
          >
            <div className="aspect-w-16 aspect-h-9 w-full min-h-[400px] flex items-center justify-center bg-slate-50">
                <button className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white shadow-xl border border-slate-100 flex items-center justify-center group hover:bg-[#005a9c] transition-all duration-300">
                  <PlayCircle className="w-10 h-10 text-[#005a9c] group-hover:text-white transition-all" />
                </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#005a9c] text-white overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-4xl font-bold mb-6">
                Ready to Transform Your AI Strategy?
              </h2>

              <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
                Join leading enterprises that trust RAKFORT for their AI governance and security needs.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group relative bg-white text-[#005a9c] font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-slate-50 flex items-center"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>REQUEST A DEMO OR CONTACT SALES</span>
                    <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AiGov;
